<!-- TVG-360: the contact's events and quotes, one line each, every line opening
     in place. Two lists rather than the three stacked bands of TVG-174. The
     figures, stages and permissions behind them are unchanged — only how they
     are laid out. -->

@if (loading) {
  <div class="mb-3 rounded-2xl w-full border border-gray-200 bg-white p-5 dark:border-gray-800 dark:bg-white/[0.03]">
    <p class="text-sm text-gray-500 dark:text-gray-400">Loading events and quotes…</p>
  </div>
} @else if (loadFailed) {
  <div class="mb-3 rounded-2xl w-full border border-gray-200 bg-white p-5 dark:border-gray-800 dark:bg-white/[0.03]">
    <p class="text-sm text-error-600 dark:text-error-400">We couldn't load this contact's events and quotes. Please refresh the page.</p>
  </div>
} @else {

  <!-- Detailed quotes ------------------------------------------------------ -->
  <div class="mb-3 rounded-2xl w-full border border-gray-200 bg-white dark:border-gray-800 dark:bg-white/[0.03]" data-testid="contact-events-panel">
    <div class="flex flex-wrap items-center justify-between gap-3 px-5 py-4 sm:px-6 sm:py-5">
      <div class="flex flex-wrap items-baseline gap-x-3 gap-y-1">
        <h3 class="text-base font-medium text-gray-800 dark:text-white/90">Detailed quotes</h3>
        <p class="text-theme-xs text-gray-500 dark:text-gray-400">One line per event. Click to open.</p>
      </div>
      @if (canCreateEvent) {
        <a
          [routerLink]="['/events/create']"
          [queryParams]="{ contact_id: contactId, status: 'Opportunity' }"
          class="inline-flex shrink-0 items-center gap-2 rounded-lg bg-brand-500 px-3 py-2 text-theme-sm font-medium text-white shadow-theme-xs hover:bg-brand-600 dark:bg-brand-600 dark:hover:bg-brand-600">
          <lucide-icon [size]="16" [name]="PlusIcon"></lucide-icon>
          <span>Create Event</span>
        </a>
      }
    </div>

    <div class="border-t border-gray-100 dark:border-gray-800">
      @if (eventItems.length === 0) {
        <p class="px-5 py-4 text-theme-sm text-gray-500 dark:text-gray-400 sm:px-6">No detailed quotes for this contact yet.</p>
      } @else {
        <!-- Column headings, desktop only — a phone line reads date, stage and total. -->
        <div class="hidden grid-cols-[22px_minmax(220px,1.6fr)_minmax(96px,0.9fr)_56px_62px_90px] gap-3 px-5 py-2 text-theme-xs text-gray-500 lg:grid dark:text-gray-400 sm:px-6">
          <span></span>
          <span>Venue and date</span>
          <span>Stage</span>
          <span class="text-right">Day</span>
          <span class="text-right">Evening</span>
          <span class="text-right">Total</span>
        </div>

        @for (item of eventItems; track eventKey(item)) {
          @let key = eventKey(item);
          <div class="border-t border-gray-100 dark:border-gray-800" data-testid="contact-event-item">
            <div
              role="button"
              tabindex="0"
              [attr.aria-expanded]="isExpanded(key)"
              (click)="toggle(key)"
              (keydown.enter)="toggle(key)"
              (keydown.space)="toggle(key); $event.preventDefault()"
              class="grid cursor-pointer grid-cols-[22px_minmax(0,1fr)_auto] items-center gap-3 px-5 py-2.5 hover:bg-gray-50 focus:outline-none focus-visible:bg-gray-50 lg:grid-cols-[22px_minmax(220px,1.6fr)_minmax(96px,0.9fr)_56px_62px_90px] dark:hover:bg-white/[0.03] dark:focus-visible:bg-white/[0.03] sm:px-6"
              data-testid="contact-event-line">
              <span
                class="flex items-center justify-center text-brand-500 transition-transform dark:text-brand-400"
                [class.rotate-90]="isExpanded(key)"
                aria-hidden="true">
                <lucide-icon [size]="16" [name]="ChevronRightIcon"></lucide-icon>
              </span>
              <span class="flex min-w-0 items-baseline gap-2">
                <span class="w-36 shrink-0 truncate text-theme-sm font-semibold text-gray-900 dark:text-white">{{ item.venue.name || 'Venue TBC' }}</span>
                <span class="min-w-0 truncate text-theme-xs text-gray-500 dark:text-gray-400">{{ formatLongDate(item.date) }}</span>
              </span>
              <span class="hidden lg:block">
                <span class="inline-flex items-center rounded-full bg-brand-100 px-2.5 py-1 text-theme-xs font-medium text-brand-800 dark:bg-brand-500/20 dark:text-brand-300">
                  {{ item.stage_label }}
                </span>
              </span>
              <span class="hidden text-right text-theme-sm text-gray-500 lg:block dark:text-gray-400">{{ item.day_guests ?? '—' }}</span>
              <span class="hidden text-right text-theme-sm text-gray-500 lg:block dark:text-gray-400">{{ item.evening_guests ?? '—' }}</span>
              <span class="text-right text-theme-sm font-semibold text-gray-900 dark:text-white">{{ formatPrice(item.headline_price) }}</span>
            </div>

            @if (isExpanded(key)) {
              <div
                class="border-t border-dashed border-gray-200 bg-gray-50/70 px-5 py-4 dark:border-gray-700 dark:bg-white/[0.02] sm:px-6 lg:pl-11"
                data-testid="contact-event-detail">
                <!-- TVG-360 (eleven a): the journey stepper used to sit here.
                     It describes the contact's progress as a customer, not this
                     event's, so it now reads once under the contact block. -->

                <!-- One strip across the width, not a column of boxes. The venue
                     name is not repeated — it is on the line above. -->
                <div class="rounded-lg border border-gray-200 bg-white px-3 py-2.5 dark:border-gray-700 dark:bg-white/[0.03]">
                  <dl class="flex flex-wrap gap-x-8 gap-y-3 text-theme-sm">
                    <div class="min-w-0">
                      <dt class="text-theme-xs text-gray-500 dark:text-gray-400">Venue contact</dt>
                      <dd class="text-gray-900 dark:text-white">
                        {{ item.detail?.contact?.name || item.detail?.contact?.email || '—' }}
                      </dd>
                    </div>
                    <div class="min-w-0">
                      <dt class="text-theme-xs text-gray-500 dark:text-gray-400">Sales manager</dt>
                      <dd class="text-gray-900 dark:text-white">{{ item.staff?.sales_manager || 'Unassigned' }}</dd>
                    </div>
                    <div class="min-w-0">
                      <dt class="text-theme-xs text-gray-500 dark:text-gray-400">Event manager</dt>
                      <dd class="text-gray-900 dark:text-white">{{ item.staff?.event_manager || 'Unassigned' }}</dd>
                    </div>
                    <div class="min-w-0 flex-1">
                      <dt class="text-theme-xs text-gray-500 dark:text-gray-400">Internal notes</dt>
                      <dd class="text-gray-900 dark:text-white">
                        @if (item.staff?.internal_notes?.length) {
                          {{ item.staff!.internal_notes[0].note }}
                        } @else {
                          None yet
                        }
                      </dd>
                    </div>
                  </dl>
                </div>

                @if (eventDate(item); as activeDate) {
                  <ng-container
                    [ngTemplateOutlet]="breakdown"
                    [ngTemplateOutletContext]="{ $implicit: activeDate }"></ng-container>
                }

                <div class="mt-3 flex flex-wrap justify-end gap-2">
                  @if (item.event_id) {
                    <button
                      type="button"
                      (click)="bookViewing.emit(item.event_id!)"
                      data-testid="contact-event-book-viewing"
                      class="inline-flex items-center rounded-lg border border-gray-200 bg-white px-3 py-2 text-theme-sm font-medium text-gray-700 hover:bg-gray-50 dark:border-gray-700 dark:bg-white/[0.03] dark:text-gray-200 dark:hover:bg-white/[0.06]">
                      Book a viewing
                    </button>
                    <a
                      [routerLink]="['/events', item.event_id]"
                      class="inline-flex items-center gap-1.5 rounded-lg bg-brand-500 px-3 py-2 text-theme-sm font-medium text-white hover:bg-brand-600 dark:bg-brand-600 dark:hover:bg-brand-600">
                      Open event
                      <lucide-icon [name]="ArrowRightIcon" [size]="16"></lucide-icon>
                    </a>
                  }
                </div>
              </div>
            }
          </div>
        }
      }
    </div>
  </div>

  <!-- Quotes ------------------------------------------------------------- -->
  <div class="mb-3 rounded-2xl w-full border border-gray-200 bg-white dark:border-gray-800 dark:bg-white/[0.03]" data-testid="contact-quotes-panel">
    <div class="flex flex-wrap items-baseline gap-x-3 gap-y-1 px-5 py-4 sm:px-6 sm:py-5">
      <h3 class="text-base font-medium text-gray-800 dark:text-white/90">Quotes</h3>
      <p class="text-theme-xs text-gray-500 dark:text-gray-400">
        One line per quoted date, newest first. Every quote sent is its own line, even for the same date.
      </p>
    </div>

    <div class="border-t border-gray-100 dark:border-gray-800">
      @if (quoteLines.length === 0) {
        <p class="px-5 py-4 text-theme-sm text-gray-500 dark:text-gray-400 sm:px-6">No quotes for this contact yet.</p>
      } @else {
        <div class="hidden grid-cols-[22px_minmax(220px,1.6fr)_minmax(96px,0.9fr)_112px_100px_56px_62px_90px] gap-3 px-5 py-2 text-theme-xs text-gray-500 lg:grid dark:text-gray-400 sm:px-6">
          <span></span>
          <span>Venue and date</span>
          <span>Received</span>
          <!-- TVG-369: tells a Provisional hold apart from a Confirmed one, and
               apart from a date that's simply free. -->
          <span>Date availability</span>
          <span>Date interest</span>
          <span class="text-right">Day</span>
          <span class="text-right">Evening</span>
          <span class="text-right">Total</span>
        </div>

        @for (line of quoteLines; track quoteKey(line)) {
          @let key = quoteKey(line);
          <div class="border-t border-gray-100 dark:border-gray-800" data-testid="contact-quote-item">
            <div
              role="button"
              tabindex="0"
              [attr.aria-expanded]="isExpanded(key)"
              (click)="toggle(key)"
              (keydown.enter)="toggle(key)"
              (keydown.space)="toggle(key); $event.preventDefault()"
              class="grid cursor-pointer grid-cols-[22px_minmax(0,1fr)_auto] items-center gap-3 px-5 py-2.5 hover:bg-gray-50 focus:outline-none focus-visible:bg-gray-50 lg:grid-cols-[22px_minmax(220px,1.6fr)_minmax(96px,0.9fr)_112px_100px_56px_62px_90px] dark:hover:bg-white/[0.03] dark:focus-visible:bg-white/[0.03] sm:px-6"
              data-testid="contact-quote-line">
              <span
                class="flex items-center justify-center text-brand-500 transition-transform dark:text-brand-400"
                [class.rotate-90]="isExpanded(key)"
                aria-hidden="true">
                <lucide-icon [size]="16" [name]="ChevronRightIcon"></lucide-icon>
              </span>
              <span class="min-w-0">
                <span class="flex min-w-0 items-baseline gap-2">
                  <span class="w-36 shrink-0 truncate text-theme-sm font-semibold text-gray-900 dark:text-white">{{ line.item.venue.name || 'Venue TBC' }}</span>
                  <span class="min-w-0 truncate text-theme-xs text-gray-500 dark:text-gray-400">{{ formatLongDate(line.date.date) }}</span>
                  <!-- TVG-326: the customer stopped seeing this quote when a date
                       was pencilled. Staff keep it, marked, so the page says
                       plainly what the two of them are each looking at. -->
                  @if (line.item.hidden_from_customer) {
                    <span
                      class="inline-flex shrink-0 items-center rounded-full bg-warning-50 px-2 py-0.5 text-theme-xs font-medium text-warning-700 dark:bg-warning-500/15 dark:text-warning-400"
                      data-testid="contact-quote-hidden-badge">
                      Hidden from customer
                    </span>
                  }
                </span>
                @if (line.date.is_preferred && line.total > 1) {
                  <span class="block truncate text-theme-xs text-gray-500 dark:text-gray-400">Preferred date</span>
                }
              </span>
              <span class="hidden text-theme-sm text-gray-500 lg:block dark:text-gray-400">{{ formatShortDate(line.item.received_at) }}</span>
              <!-- TVG-369: all three answers are coloured, so a free date, a
                   provisional hold and a confirmed one read apart at a glance.
                   A date we have no read on stays blank rather than guessing. -->
              <span class="hidden lg:block" data-testid="contact-quote-availability">
                @if (availabilityBadge(line.date); as badge) {
                  <span
                    class="inline-flex items-center rounded-full px-2.5 py-1 text-theme-xs font-medium"
                    [class.bg-blue-100]="badge.tone === 'blue'"
                    [class.text-blue-800]="badge.tone === 'blue'"
                    [ngClass]="{ 'dark:bg-blue-900/30': badge.tone === 'blue', 'dark:bg-warning-900/30': badge.tone === 'amber', 'dark:bg-success-900/30': badge.tone === 'green' }"
                    [class.dark:text-blue-300]="badge.tone === 'blue'"
                    [class.bg-warning-100]="badge.tone === 'amber'"
                    [class.text-warning-800]="badge.tone === 'amber'"
                    [class.dark:text-warning-400]="badge.tone === 'amber'"
                    [class.bg-success-100]="badge.tone === 'green'"
                    [class.text-success-800]="badge.tone === 'green'"
                    [class.dark:text-success-400]="badge.tone === 'green'">
                    {{ badge.label }}
                  </span>
                }
              </span>
              <!-- TVG-369: purely informational — other people's interest in
                   this date, independent of whether it's still available. -->
              <span class="hidden lg:block" data-testid="contact-quote-interest">
                @if (interestLabel(line.date); as interest) {
                  <span class="inline-flex items-center rounded-full bg-warning-50 px-2.5 py-1 text-theme-xs font-medium text-warning-700 dark:bg-warning-500/15 dark:text-warning-400">
                    {{ interest }}
                  </span>
                }
              </span>
              <span class="hidden text-right text-theme-sm text-gray-500 lg:block dark:text-gray-400">{{ line.item.day_guests ?? '—' }}</span>
              <span class="hidden text-right text-theme-sm text-gray-500 lg:block dark:text-gray-400">{{ line.item.evening_guests ?? '—' }}</span>
              <span class="text-right text-theme-sm font-semibold text-gray-900 dark:text-white">{{ formatPrice(line.date.total ?? line.item.headline_price) }}</span>
            </div>

            @if (isExpanded(key)) {
              <div
                class="border-t border-dashed border-gray-200 bg-gray-50/70 px-5 py-4 dark:border-gray-700 dark:bg-white/[0.02] sm:px-6 lg:pl-11"
                data-testid="contact-quote-detail">
                <!-- TVG-326: nothing is deleted — say in words why the customer
                     no longer has this quote on their home page. -->
                @if (line.item.hidden_from_customer) {
                  <p
                    class="mb-3 rounded-lg border border-warning-200 bg-warning-25 px-3 py-2 text-theme-sm text-warning-700 dark:border-warning-500/30 dark:bg-warning-500/10 dark:text-warning-400"
                    data-testid="contact-quote-hidden-note">
                    Hidden from the customer. A date has been pencilled, so this quote no longer shows on their home page. It is kept here in full.
                  </p>
                }

                <div class="rounded-lg border border-gray-200 bg-white px-3 py-2.5 dark:border-gray-700 dark:bg-white/[0.03]">
                  <dl class="flex flex-wrap gap-x-8 gap-y-3 text-theme-sm">
                    <div class="min-w-0">
                      <dt class="text-theme-xs text-gray-500 dark:text-gray-400">Venue contact</dt>
                      <dd class="text-gray-900 dark:text-white">
                        {{ line.item.detail?.contact?.name || line.item.detail?.contact?.email || '—' }}
                      </dd>
                    </div>
                    <div class="min-w-0">
                      <dt class="text-theme-xs text-gray-500 dark:text-gray-400">Quote sent</dt>
                      <dd class="text-gray-900 dark:text-white">{{ formatDateTime(line.item.received_at) }}</dd>
                    </div>
                    <!-- TVG-377: quotes from the same enquiry are never grouped,
                         so the line does not list the enquiry's other dates. -->
                  </dl>
                </div>

                <ng-container
                  [ngTemplateOutlet]="breakdown"
                  [ngTemplateOutletContext]="{ $implicit: line.date }"></ng-container>

                @if (demandLabel(line.date.other_enquiries); as demand) {
                  <p class="mt-2 text-theme-sm text-gray-500 dark:text-gray-400">{{ demand }}</p>
                }

                <div class="mt-3 flex justify-end">
                  <button
                    type="button"
                    (click)="progressToDetailedQuote(line)"
                    [disabled]="!line.date.date"
                    data-testid="contact-quote-progress"
                    class="inline-flex items-center rounded-lg bg-brand-500 px-3 py-2 text-theme-sm font-medium text-white hover:bg-brand-600 disabled:cursor-not-allowed disabled:opacity-50 dark:bg-brand-600 dark:hover:bg-brand-600">
                    Progress to detailed quote
                  </button>
                </div>
              </div>
            }
          </div>
        }
      }
    </div>
  </div>
}

<!-- TVG-360: the priced breakdown for one date, shared by an expanded event and
     an expanded quote. Nothing about the figures changed; only where the table
     sits. -->
<ng-template #breakdown let-date>
  @if (date.lines.length > 0) {
    <div class="mt-3 overflow-x-auto rounded-lg border border-gray-200 dark:border-gray-700">
      <table class="w-full text-left text-theme-sm">
        <thead class="bg-gray-100 text-theme-xs text-gray-500 dark:bg-white/[0.04] dark:text-gray-400">
          <tr>
            <th class="px-3 py-2 font-medium">Item</th>
            <th class="px-3 py-2 text-right font-medium">Day</th>
            <th class="px-3 py-2 text-right font-medium">Evening</th>
            <th class="px-3 py-2 text-right font-medium">Unit × qty</th>
            <th class="px-3 py-2 text-right font-medium">Subtotal</th>
          </tr>
        </thead>
        <tbody class="divide-y divide-gray-100 bg-white dark:divide-gray-800 dark:bg-white/[0.02]">
          @for (line of date.lines; track $index) {
            <tr class="text-gray-700 dark:text-gray-200">
              <td class="px-3 py-2">
                <span class="font-medium text-gray-900 dark:text-white">{{ line.category }}</span>
                @if (line.item && line.item !== line.category) {
                  <span class="ml-1.5 font-normal text-theme-xs text-gray-500 dark:text-gray-400">{{ line.item }}</span>
                }
              </td>
              <td class="px-3 py-2 text-right">{{ lineGuestCounts(line).day ?? '—' }}</td>
              <td class="px-3 py-2 text-right">{{ lineGuestCounts(line).evening ?? '—' }}</td>
              <td class="px-3 py-2 text-right">{{ lineUnitTimesQuantity(line.unit_price, line.quantity) }}</td>
              <td class="px-3 py-2 text-right font-medium">{{ formatPrice(line.subtotal) }}</td>
            </tr>
          }
        </tbody>
        <tfoot class="border-t border-gray-200 bg-gray-50 dark:border-gray-700 dark:bg-white/[0.03]">
          <tr class="font-semibold text-gray-900 dark:text-white">
            <td class="px-3 py-2" colspan="4">Total</td>
            <td class="px-3 py-2 text-right">{{ formatPrice(date.total) }}</td>
          </tr>
        </tfoot>
      </table>
    </div>
  } @else {
    <p class="mt-3 text-theme-sm text-gray-500 dark:text-gray-400">No detailed breakdown is available for this item.</p>
  }
</ng-template>
